/**
 * Unified lessons registry
 * Central location for all lesson configurations across all courses
 *
 * Uses dynamic imports for code splitting - each course config loads on demand
 */

import { normalizeLessonNumber } from '../utils/lessonUtils.js';
import { normalizeCourseId } from '../utils/courseNormalization.js';

/**
 * Dynamic loaders for each course config
 * Vite will split each into its own chunk
 */
const configLoaders = {
  phonics: () => import('../courses/phonicsConfig.json').then(m => m.default),
  piggy: () => import('../courses/piggyConfig.json').then(m => m.default),
  songs: () => import('../courses/songsConfig.json').then(m => m.default),
  // Curated bundle: phonics b1/b2 + songs merged into ONE flat list under book 'b1',
  // renumbered 001-081 (unique keys; findLesson matches on book+lessonNumber). Composed
  // at load time so it auto-tracks phonics/songs video re-wiring; new lesson objects
  // (spread) with original videos/pages shared read-only. Each lesson carries a
  // per-lesson `layout` so VocabStep renders phonics single (letters) and songs tabbed
  // (歌词) even though they share one book.
  level1zero: async () => {
    const [phonics, songs] = await Promise.all([
      import('../courses/phonicsConfig.json').then(m => m.default),
      import('../courses/songsConfig.json').then(m => m.default),
    ]);
    const src = [
      ...phonics.filter(l => l.book === 'b1' || l.book === 'b2').map(l => ({ lesson: l, layout: 'single' })),
      ...songs.map(l => ({ lesson: l, layout: 'tabbed' })),
    ];
    return src.map(({ lesson, layout }, i) => ({
      ...lesson,
      course: 'level1zero',
      book: 'b1',
      seq: i + 1,
      lessonNumber: String(i + 1).padStart(3, '0'),
      layout,
    }));
  },
  southstreet: () => import('../courses/southstreetConfig.json').then(m => m.default),
  sscartoons: () => import('../courses/sscartoonsConfig.json').then(m => m.default),
  atomichabits: () => import('../courses/atomichabitsConfig.json').then(m => m.default),
  navalmanack: () => import('../courses/navalmanackConfig.json').then(m => m.default),
  gettingthingsdone: () => import('../courses/gettingThingsDoneConfig.json').then(m => m.default),
  top10speeches: () => import('../courses/top10SpeechesConfig.json').then(m => m.default),
  '100fans': () => import('../courses/100FansConfig.json').then(m => m.default),
  whywesleep: () => import('../courses/whyWeSleepConfig.json').then(m => m.default),
  shawshank: () => import('../courses/shawshankConfig.json').then(m => m.default),
  nce1: () => import('../courses/nce1Config.json').then(m => m.default),
  nce2: () => import('../courses/nce2Config.json').then(m => m.default),
  nce3: () => import('../courses/nce3Config.json').then(m => m.default),
  nce4: () => import('../courses/nce4Config.json').then(m => m.default),
  classicchinesestories: () => import('../courses/classicChineseStoriesConfig.json').then(m => m.default),
  monkeyking: () => import('../courses/monkeyKingConfig.json').then(m => m.default),
  ipa: () => import('../features/ipaReference/data/ipaData').then(m => m.getIPALessons()),
  'sight-words': () => import('../courses/sightWordsConfig.json').then(m => m.default),
  sentences: () => import('../courses/sentencesConfig.json').then(m => m.default),
  dolch: () => import('../courses/dolchConfig.json').then(m => m.default),
  categories: () => import('../courses/categoriesConfig.json').then(m => m.default),
  readwithselina: () => import('../courses/readWithSelinaConfig.json').then(m => m.default),
};

/**
 * Cache for loaded configs - avoids re-fetching
 * Stores resolved lesson arrays
 */
const loadedConfigs = {};

/**
 * Cache for in-flight config loads - stores promises
 * This allows preloading to start the fetch and later calls to await the same promise
 */
const pendingLoads = {};

/**
 * Preload course config for performance optimization
 * Call this early (e.g., on route match) to start loading before component mounts
 * @param {string} course - Course name
 */
export function preloadCourseConfig(course) {
  const normalizedCourse = normalizeCourseId(course);

  // Already loaded or loading
  if (loadedConfigs[normalizedCourse] || pendingLoads[normalizedCourse]) {
    return;
  }

  const loader = configLoaders[normalizedCourse];
  if (!loader) return;

  // Start the load immediately (don't await) and store the promise
  pendingLoads[normalizedCourse] = loader()
    .then(lessons => {
      loadedConfigs[normalizedCourse] = lessons;
      delete pendingLoads[normalizedCourse];
      return lessons;
    })
    .catch(err => {
      delete pendingLoads[normalizedCourse];
      throw err;
    });
}

/**
 * Load lessons for a course (lazy, cached)
 * @param {string} course - Course name
 * @returns {Promise<Array>} - Array of lesson configurations
 */
export async function getLessonsByCourse(course) {
  const normalizedCourse = normalizeCourseId(course);

  // Return cached if available
  if (loadedConfigs[normalizedCourse]) {
    return loadedConfigs[normalizedCourse];
  }

  // If preload started, await that promise
  if (pendingLoads[normalizedCourse]) {
    return pendingLoads[normalizedCourse];
  }

  // Load and cache
  const loader = configLoaders[normalizedCourse];
  if (!loader) return [];

  const lessons = await loader();
  loadedConfigs[normalizedCourse] = lessons;
  return lessons;
}

/**
 * Find a specific lesson by course, book, and lesson number
 * @param {string} course - Course name (phonics, songs, piggy, southstreet)
 * @param {string} book - Book number (e.g., 'b1')
 * @param {string|number} lessonNumber - Lesson number (e.g., '01' or '001')
 * @returns {Promise<Object|null>} - The lesson configuration or null if not found
 */
export async function findLesson(course, book, lessonNumber) {
  const list = await getLessonsByCourse(course);
  const normalized = normalizeLessonNumber(lessonNumber);

  return list.find(
    l => l.book === book && l.lessonNumber === normalized
  ) || null;
}

/**
 * Get all lessons for a specific course and book
 * @param {string} course - Course name
 * @param {string} book - Book number
 * @returns {Promise<Array>} - Array of lesson configurations
 */
export async function getLessonsByBook(course, book) {
  const lessons = await getLessonsByCourse(course);
  return lessons.filter(l => l.book === book);
}
